import { z } from 'zod';

/**
 * ALL money in this product is an integer number of paise. Never a float, never rupees.
 * A "float rupee" bug in a tailoring shop's ledger is invisible until an accountant finds
 * it a year later, so the type system and these helpers are the only sanctioned way in and
 * out of money values.
 */
export type Paise = number;

/** Money as it is stored and transmitted: an integer count of paise. */
export const paiseSchema = z
  .number()
  .int('Amounts must be an integer number of paise')
  .finite()
  .describe('Amount in paise');

/** A money amount that cannot be negative (a price, a payment). */
export const nonNegativePaiseSchema = paiseSchema.min(0, 'Amount cannot be negative');

export const PAISE_PER_RUPEE = 100;

/**
 * Round to a whole paise, half away from zero (2.5 -> 3, -2.5 -> -3), correcting for
 * binary floating point error first so `19.99 * 100` does not become 1998 paise.
 */
export function roundToPaise(value: number): Paise {
  if (!Number.isFinite(value)) {
    throw new RangeError(`Cannot round a non-finite amount: ${value}`);
  }
  if (Number.isInteger(value)) return value;
  const sign = value < 0 ? -1 : 1;
  const magnitude = Math.abs(value);
  // 12 significant digits is far beyond any real shop amount and removes the 0.30000000000000004 class of error.
  const corrected = Number(magnitude.toPrecision(12));
  return sign * Math.floor(corrected + 0.5);
}

/** Convert a rupee number to paise. Prefer {@link parseRupees} for user input. */
export function rupeesToPaise(rupees: number): Paise {
  return roundToPaise(rupees * PAISE_PER_RUPEE);
}

/** Convert paise to a rupee number. For display and export only — never for arithmetic. */
export function paiseToRupees(paise: Paise): number {
  assertPaise(paise);
  return paise / PAISE_PER_RUPEE;
}

/**
 * Parse user input ("1,234.50", "₹499", "  12.5  ") into paise using decimal — not binary —
 * arithmetic, so what the shop typed is what gets stored. More than two decimal places are
 * rounded half up. Throws on anything that is not a number.
 */
export function parseRupees(input: string | number): Paise {
  if (typeof input === 'number') return rupeesToPaise(input);

  const cleaned = input.replace(/[₹,\s_]/g, '');
  if (!/^[+-]?(\d+(\.\d*)?|\.\d+)$/.test(cleaned)) {
    throw new TypeError(`Not a valid rupee amount: "${input}"`);
  }

  const negative = cleaned.startsWith('-');
  const unsigned = cleaned.replace(/^[+-]/, '');
  const [wholePart = '', fractionPart = ''] = unsigned.split('.');
  // Scale to milli-rupees so we can round the third decimal place ourselves.
  const scaled =
    BigInt(wholePart === '' ? '0' : wholePart) * 1000n +
    BigInt(`${fractionPart}000`.slice(0, 3) || '0');
  const quotient = scaled / 10n;
  const remainder = scaled % 10n;
  const rounded = remainder >= 5n ? quotient + 1n : quotient;
  return Number(negative ? -rounded : rounded);
}

/** Format paise for display. Defaults to Indian digit grouping with the ₹ symbol. */
export function formatPaise(
  paise: Paise,
  options: { withSymbol?: boolean; locale?: string; fractionDigits?: 0 | 2 } = {},
): string {
  assertPaise(paise);
  const { withSymbol = true, locale = 'en-IN', fractionDigits = 2 } = options;
  const formatter = new Intl.NumberFormat(
    locale,
    withSymbol
      ? {
          style: 'currency',
          currency: 'INR',
          minimumFractionDigits: fractionDigits,
          maximumFractionDigits: fractionDigits,
        }
      : {
          minimumFractionDigits: fractionDigits,
          maximumFractionDigits: fractionDigits,
        },
  );
  return formatter.format(paiseToRupees(paise));
}

export function isPaise(value: unknown): value is Paise {
  return typeof value === 'number' && Number.isInteger(value);
}

export function assertPaise(value: number): asserts value is Paise {
  if (!Number.isInteger(value)) {
    throw new TypeError(`Money must be an integer number of paise, received ${value}`);
  }
}

export function sumPaise(amounts: Iterable<Paise>): Paise {
  let total = 0;
  for (const amount of amounts) {
    assertPaise(amount);
    total += amount;
  }
  return total;
}

/** Multiply an amount by a quantity (which may be fractional, e.g. 2.5 metres of fabric). */
export function multiplyPaise(paise: Paise, quantity: number): Paise {
  assertPaise(paise);
  return roundToPaise(paise * quantity);
}

/** `percent` of `paise`, rounded to whole paise. `percentOf(10000, 18)` is 1800. */
export function percentOf(paise: Paise, percent: number): Paise {
  assertPaise(paise);
  return roundToPaise((paise * percent) / 100);
}

/** Subtract a percentage discount, returning the remaining amount. */
export function applyDiscountPercent(paise: Paise, percent: number): Paise {
  return paise - percentOf(paise, percent);
}

/**
 * Split `total` across `weights` so the parts always add back up to `total` exactly
 * (largest-remainder method). Used for tax splits, discount apportionment and any other
 * place where a naive per-line rounding would lose or invent a paisa.
 */
export function allocatePaise(total: Paise, weights: readonly number[]): Paise[] {
  assertPaise(total);
  if (weights.length === 0) return [];
  if (weights.some((weight) => weight < 0)) {
    throw new RangeError('Allocation weights cannot be negative');
  }

  const weightTotal = weights.reduce((sum, weight) => sum + weight, 0);
  if (weightTotal === 0) {
    // Nothing to weight by: give it all to the first bucket rather than dividing by zero.
    return weights.map((_, index) => (index === 0 ? total : 0));
  }

  const exact = weights.map((weight) => (total * weight) / weightTotal);
  const floored = exact.map((value) => Math.floor(value));
  let remaining = total - floored.reduce((sum, value) => sum + value, 0);

  const order = exact
    .map((value, index) => ({ index, remainder: value - Math.floor(value) }))
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index);

  const result = [...floored];
  for (const { index } of order) {
    if (remaining === 0) break;
    const step = remaining > 0 ? 1 : -1;
    result[index] = (result[index] ?? 0) + step;
    remaining -= step;
  }
  return result;
}

/**
 * Round an amount to whole rupees and report the adjustment, for the "rounding off" line
 * Indian invoices carry so the printed total always reconciles.
 */
export function roundToRupee(paise: Paise): { roundedPaise: Paise; roundingOffPaise: Paise } {
  assertPaise(paise);
  const roundedPaise = roundToPaise(paise / PAISE_PER_RUPEE) * PAISE_PER_RUPEE;
  return { roundedPaise, roundingOffPaise: roundedPaise - paise };
}
